// Lab 2 safety invariants: one primary per acknowledged view, replies only
// from the view's primary, backup state inclusion, per-replica at-most-once,
// and client result correspondence.
export function singlePrimaryPerView(state) {
  const byView = {};
  state.config.servers.forEach((id) => {
    const node = state.nodes[id];
    if (!node) return;
    const d = node.durable;
    if (d.status === 'active' && d.view.primary === id) {
      byView[d.view.n] = byView[d.view.n] || [];
      byView[d.view.n].push(id);
    }
  });
  const bad = Object.keys(byView).find((n) => byView[n].length > 1);
  if (bad) {
    return {
      invariant: 'lab2-single-primary',
      message: 'view ' + bad + ' has primaries ' + byView[bad].join(','),
    };
  }
  return null;
}

export function servedOnlyByViewPrimary(state) {
  const vs = state.nodes[state.config.viewServer];
  if (!vs) return null;
  const primaryOf = {};
  vs.durable.history.forEach((v) => {
    primaryOf[v.n] = v.primary;
  });
  for (const id of state.config.servers) {
    const node = state.nodes[id];
    if (!node) continue;
    const bad = node.durable.served.find((s) => primaryOf[s.view] !== id);
    if (bad) {
      return {
        invariant: 'lab2-serve-role',
        message: id + ' served view ' + bad.view + ' without being its primary',
      };
    }
  }
  return null;
}

// A synchronized backup mirrors every operation before the primary answers,
// so its applied set always includes the primary's.
export function backupIncludesPrimary(state) {
  const primaries = {};
  state.config.servers.forEach((id) => {
    const node = state.nodes[id];
    if (!node) return;
    const d = node.durable;
    if (d.status === 'active' && d.view.primary === id) {
      primaries[d.view.n] = d;
    }
  });
  for (const id of state.config.servers) {
    const node = state.nodes[id];
    if (!node) continue;
    const d = node.durable;
    if (d.status !== 'active' || d.view.backup !== id) continue;
    const primary = primaries[d.view.n];
    if (!primary) continue;
    const missing = primary.appliedIds.find((x) => !d.appliedIds.includes(x));
    if (missing) {
      return {
        invariant: 'lab2-backup-inclusion',
        message: 'backup ' + id + ' lacks ' + missing + ' the primary applied',
      };
    }
  }
  return null;
}

export function atMostOncePerReplica(state) {
  for (const id of state.config.servers) {
    const node = state.nodes[id];
    if (!node) continue;
    const seen = new Set();
    for (const x of node.durable.appliedIds) {
      if (seen.has(x)) {
        return {
          invariant: 'lab2-at-most-once',
          message: id + ' applied ' + x + ' more than once',
        };
      }
      seen.add(x);
    }
  }
  return null;
}

export function clientResultCorrespondence(state) {
  const applied = new Set();
  state.config.servers.forEach((id) => {
    const node = state.nodes[id];
    if (node) node.durable.appliedIds.forEach((x) => applied.add(x));
  });
  for (const c of state.config.clients) {
    const node = state.nodes[c];
    if (!node) continue;
    const orphan = node.durable.completed.find((r) => !applied.has(c + '#' + r.seq));
    if (orphan) {
      return {
        invariant: 'lab2-result-correspondence',
        message: 'client ' + c + ' completed seq ' + orphan.seq + ' no replica applied',
      };
    }
  }
  return null;
}

export function checkLab2Invariants(state) {
  return [
    singlePrimaryPerView,
    servedOnlyByViewPrimary,
    backupIncludesPrimary,
    atMostOncePerReplica,
    clientResultCorrespondence,
  ]
    .map((fn) => fn(state))
    .filter(Boolean);
}
